import { useQuery } from '@tanstack/react-query';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { fetchCategories, fetchCategoryBySlug, fetchBrands, fetchProducts, fetchProductBySlug, fetchBanners } from '../api/catalog-api.js';
import type { ProductListParams } from '../types/catalog.js';

/**
 * Every catalog/banner read is public (no Clerk token) and edge-cached by
 * the backend for 60s (see `docs/catalog-architecture.md` "Caching") --
 * these hooks lean on that with a matching `staleTime` rather than
 * refetching more often than the backend itself would ever return fresh
 * data for.
 */
const CATALOG_STALE_TIME = 60_000;

export function useCategories(params: { parentId?: string; limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: ['storefront', 'categories', params],
    queryFn: ({ signal }) => fetchCategories(params, { signal }),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: ['storefront', 'category', slug],
    queryFn: ({ signal }) => fetchCategoryBySlug(slug!, { signal }),
    enabled: Boolean(slug),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useBrands(params: { limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: ['storefront', 'brands', params],
    queryFn: ({ signal }) => fetchBrands(params, { signal }),
    staleTime: CATALOG_STALE_TIME,
  });
}

export interface UseProductsOptions {
  /**
   * Standard TanStack Query gate -- lets a caller build `params` from data
   * that may not exist yet (e.g. a related-products query keyed off
   * `product.categoryId` before the product itself has loaded) without the
   * query firing on a placeholder/empty value. Defaults to `true`, so every
   * existing call site (`useProducts(params)`, no second argument) keeps
   * firing immediately exactly as before -- this is additive, not a
   * behavior change for anyone not passing it.
   */
  enabled?: boolean;
}

export function useProducts(params: ProductListParams, options: UseProductsOptions = {}) {
  return useQuery({
    queryKey: ['storefront', 'products', params],
    queryFn: ({ signal }) => fetchProducts(params, { signal }),
    staleTime: CATALOG_STALE_TIME,
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: ['storefront', 'product', slug],
    queryFn: ({ signal }) => fetchProductBySlug(slug!, { signal }),
    enabled: Boolean(slug),
    staleTime: CATALOG_STALE_TIME,
    retry: (count, error) => !(error instanceof StorefrontApiError && error.status === 404) && count < 2,
  });
}

export function useBanners(placement: string) {
  return useQuery({
    queryKey: ['storefront', 'banners', placement],
    queryFn: ({ signal }) => fetchBanners(placement, { signal }),
    staleTime: CATALOG_STALE_TIME,
  });
}
